// src/services/authService.js
class AuthService {
  static normalizeRole = (role) => {
    if (!role) return '';
    const value = typeof role === 'object' ? role.name || role.Name || role.role || role.Role : role;
    const normalized = String(value).trim().toLowerCase();
    if (normalized === 'admin' || normalized === 'administrator') return 'Admin';
    if (normalized === 'user') return 'User';
    return String(value).trim();
  };

  static normalizeRoles = (roles) => {
    const list = Array.isArray(roles) ? roles : [roles];
    return [...new Set(list.map(this.normalizeRole).filter(Boolean))];
  };

  static getPrimaryRole = (user) => {
    const roles = this.normalizeRoles(user?.roles?.length ? user.roles : user?.role);
    return roles[0] || 'User';
  };

  // Core auth base (login / refresh / logout / me)
  static get authBase() {
    const base = window.APP_CONFIG?.coreAuthBase || 'http://localhost:8080/core';
    return `${base}/auth`;
  }

  // DpgFinance business API base (unchanged)
  static get apiBase() {
    return window.APP_CONFIG?.apiUrl || 'https://api.dpg.net.tr';
  }

  static get apiTimeout() {
    return window.APP_CONFIG?.apiTimeout || 30000;
  }

  // Login process - Fidost Core auth
  static login = async (credentials) => {
    try {
      const payload = {
        email: credentials.email,
        password: credentials.password,
        rememberMe: credentials.rememberMe || false
      };

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.apiTimeout);

      const response = await fetch(`${this.authBase}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.accessToken) {
        if (data.mfaRequired) {
          return { success: false, mfaRequired: true };
        }

        // Normalise: Core returns roles[] array; keep role (first entry) for
        // backward-compat with existing UI code that reads user.role.
        const roles = this.normalizeRoles(data.user?.roles || []);
        return {
          success: true,
          data: {
            token: data.accessToken,
            refreshToken: data.refreshToken,
            expiresInSec: data.expiresInSec,
            user: {
              id: data.user?.id,
              email: data.user?.email,
              name: data.user?.name,
              surname: data.user?.surname,
              role: roles[0] || 'User',
              roles
            }
          }
        };
      } else {
        return {
          success: false,
          message: data.message || data.error || 'Email address or password is incorrect.'
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: 'Server error. Please try again later.'
      };
    }
  };

  // Registration process - REAL BACKEND CALL
  static register = async (formData) => {
    try {
      // Map frontend field names to backend DTO
      const payload = {
        CompanyName: formData.companyName,
        TaxNumber: formData.taxNumber,
        CompanyType: formData.companyType,
        BusinessArea: formData.businessArea,
        City: formData.city,
        Address: formData.address,
        Name: formData.name,
        Surname: formData.surname,
        Title: formData.title,
        Email: formData.email,
        PhoneNumber: formData.phone,
        Password: formData.password,
        ConfirmPassword: formData.confirmPassword,
        AcceptTerms: !!formData.termsAccepted,
      };

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.apiTimeout);

      const response = await fetch(`${this.apiBase}/api/auth/register`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const data = await response.json();

      if (response.ok && data?.success) {
        return { success: true, data };
      }

      const message = data?.Message || 'Registration failed.';
      return { success: false, message };
    } catch (error) {
      console.error('Register error:', error);
      return { success: false, message: 'Server error. Please try again later.' };
    }
  };

  // Logout process - Core auth (fire-and-forget)
  static logoutFromServer = async () => {
    try {
      const token = this.getAuthToken();
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) return true;

      fetch(`${this.authBase}/logout`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken })
      }).catch(() => {
        // Ignore errors - we're logging out anyway
      });

      return true; // Return immediately
    } catch (error) {
      // Ignore errors - we're logging out anyway
      return true;
    }
  };

  // Current User Info - Core auth
  static getCurrentUserFromServer = async () => {
    try {
      const token = this.getAuthToken();
      if (!token) return null;

      const response = await fetch(`${this.authBase}/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      }

      if (response.status === 401) {
        // Token invalid, logout
        this.logout(false);
        return null;
      }

      return null;
    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  };

  // Save token and user data (also stores refreshToken)
  static setAuthData = (token, userData, rememberMe = false, refreshToken = null) => {
    const storage = rememberMe ? localStorage : sessionStorage;
    
    // Clear previous auth data from both storages
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('rememberMe');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('userData');
    sessionStorage.removeItem('tokenExpiry');
    sessionStorage.removeItem('rememberMe');
    
    // Store new auth data in appropriate storage
    storage.setItem('authToken', token);
    if (refreshToken) storage.setItem('refreshToken', refreshToken);
    const normalizedRoles = this.normalizeRoles(userData?.roles?.length ? userData.roles : userData?.role);
    const normalizedUserData = {
      ...userData,
      role: normalizedRoles[0] || 'User',
      roles: normalizedRoles.length ? normalizedRoles : ['User']
    };
    storage.setItem('userData', JSON.stringify(normalizedUserData));
    
    // Token expiry time - rememberMe: 30 days, normal: 8 hours
    const expiryTime = Date.now() + (rememberMe ? 30 * 24 * 60 * 60 * 1000 : 8 * 60 * 60 * 1000);
    storage.setItem('tokenExpiry', expiryTime.toString());
    storage.setItem('rememberMe', rememberMe.toString());
  };

  // Token check
  static getAuthToken = () => {
    // Check localStorage first, then sessionStorage
    let token = localStorage.getItem('authToken');
    let expiry = localStorage.getItem('tokenExpiry');

    if (!token) {
      token = sessionStorage.getItem('authToken');
      expiry = sessionStorage.getItem('tokenExpiry');
    }

    // Token expiry check
    if (token && expiry) {
      const now = Date.now();
      if (now > parseInt(expiry)) {
        this.logout();
        return null;
      }
    }

    return token;
  };

  // Refresh token retrieval
  static getRefreshToken = () => {
    return localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken') || null;
  };

  // Get user data
  static getCurrentUser = () => {
    try {
      let userData = localStorage.getItem('userData');
      
      if (!userData) {
        userData = sessionStorage.getItem('userData');
      }
      
      if (!userData) return null;

      const user = JSON.parse(userData);
      const normalizedRoles = this.normalizeRoles(user?.roles?.length ? user.roles : user?.role);
      return {
        ...user,
        role: normalizedRoles[0] || 'User',
        roles: normalizedRoles.length ? normalizedRoles : ['User']
      };
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  };

  // Update user data (preserves token and rememberMe setting)
  static updateUserData = (updatedUserData) => {
    try {
      const token = this.getAuthToken();
      if (!token) return false;

      // Determine which storage is being used based on where token is stored
      let storage = localStorage;
      if (!localStorage.getItem('authToken')) {
        storage = sessionStorage;
      }
      
      // Update user data in the same storage
      storage.setItem('userData', JSON.stringify(updatedUserData));
      
      return true;
    } catch (error) {
      console.error('Error updating user data:', error);
      return false;
    }
  };

  // Check authentication status
  static isAuthenticated = () => {
    const token = this.getAuthToken();
    const user = this.getCurrentUser();
    return !!(token && user);
  };

  // Logout process
  static logout = (showToast = true) => {
    // Clear all auth data first (to prevent any race conditions)
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('rememberMe');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('userData');
    sessionStorage.removeItem('tokenExpiry');
    sessionStorage.removeItem('rememberMe');
    
    // Dispatch logout event immediately
    window.dispatchEvent(new CustomEvent('userLogout'));
    
    // Send logout request to backend in background (fire-and-forget)
    this.logoutFromServer().catch(() => {});
    
    // Redirect to login page
    window.location.replace(window.location.origin);
  };

  // Session timeout check
  static checkSessionTimeout = () => {
    const expiry = localStorage.getItem('tokenExpiry') || sessionStorage.getItem('tokenExpiry');
    
    if (expiry) {
      const now = Date.now();
      const timeLeft = parseInt(expiry) - now;
      
      // Warning 5 minutes before
      if (timeLeft > 0 && timeLeft < 5 * 60 * 1000) {
        if (window.showToast) {
          window.showToast('Your session will expire soon', 'warning');
        }
      }
      
      // Session expired
      if (timeLeft <= 0) {
        if (window.showToast) {
          window.showToast('Your session has expired. Please sign in again.', 'error');
        }
        this.logout(false);
      }
    }
  };

  // Refresh access token using Core's /auth/refresh endpoint.
  // Returns true on success, false on failure.
  static refresh = async () => {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) return false;

      const rememberMe = (localStorage.getItem('rememberMe') || sessionStorage.getItem('rememberMe')) === 'true';

      const response = await fetch(`${this.authBase}/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken, rememberMe })
      });

      if (!response.ok) return false;

      const data = await response.json();
      if (!data.accessToken) return false;

      const storage = localStorage.getItem('refreshToken') ? localStorage : sessionStorage;
      storage.setItem('authToken', data.accessToken);
      if (data.refreshToken) storage.setItem('refreshToken', data.refreshToken);

      const expiryTime = Date.now() + (rememberMe ? 30 * 24 * 60 * 60 * 1000 : 8 * 60 * 60 * 1000);
      storage.setItem('tokenExpiry', expiryTime.toString());

      return true;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  };

  // Legacy alias (kept for any existing callers)
  static refreshToken = () => this.refresh();

  // Authorized headers for API calls
  static getAuthHeaders = () => {
    const token = this.getAuthToken();
    return token ? {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    } : {
      'Content-Type': 'application/json'
    };
  };

  // Generic API call method
  static apiCall = async (endpoint, options = {}) => {
    try {
      const url = `${this.apiBase}${endpoint}`;
      const headers = options.requireAuth !== false ? this.getAuthHeaders() : { 'Content-Type': 'application/json' };
      
      const config = {
        headers,
        ...options,
      };

      const response = await fetch(url, config);
      
      if (response.status === 401 && options.requireAuth !== false) {
        // Unauthorized — try refresh then retry once
        const refreshed = await this.refresh();
        if (!refreshed) {
          this.logout(true);
          return null;
        }
        const retryResponse = await fetch(url, { ...config, headers: this.getAuthHeaders() });
        if (retryResponse.status === 401) {
          this.logout(true);
          return null;
        }
        const retryData = await retryResponse.json().catch(() => ({}));
        return { response: retryResponse, data: retryData };
      }

      const data = await response.json().catch(() => ({}));
      return { response, data };
    } catch (error) {
      console.error('API call error:', error);
      return null;
    }
  };

  // Role check — supports both string role and roles[]
  static hasRole = (requiredRole) => {
    const user = this.getCurrentUser();
    if (!user) return false;
    const required = this.normalizeRole(requiredRole);
    const roles = this.normalizeRoles(user.roles?.length ? user.roles : user.role);
    return roles.includes(required);
  };

  // Permission check
  static hasPermission = (permission) => {
    const user = this.getCurrentUser();
    if (!user) return false;
    
    // Role-based permissions
    const rolePermissions = {
      'Admin': ['user.create', 'user.read', 'user.update', 'user.delete', 'limit.create', 'limit.read', 'limit.update'],
      'Buyer': ['credit.create', 'credit.read', 'profile.read', 'profile.update'],
      'Seller': ['guarantee.read', 'guarantee.approve', 'guarantee.reject', 'profile.read', 'profile.update']
    };
    
    return rolePermissions[user.role]?.includes(permission) || false;
  };

  // Health check
  static checkApiHealth = async () => {
    try {
      const response = await fetch(`${this.apiBase}/health`);
      return response.ok;
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  };

  // --- Core auth helpers (stubs — endpoints to be added in Core later) ---

  static forgotPassword = async (_email) => {
    return { success: false, message: 'Forgot-password endpoint not yet available.' };
  };

  static resetPassword = async (_token, _newPassword) => {
    return { success: false, message: 'Reset-password endpoint not yet available.' };
  };

  static enrollMfa = async () => {
    return { success: false, message: 'MFA enrollment not yet available.' };
  };

  static verifyMfa = async (_code) => {
    return { success: false, message: 'MFA verification not yet available.' };
  };
}

export default AuthService;
